// Sprint 11 A2: Briefing Metrics Panel — vs yesterday with trend arrows
import { Skeleton } from "@/components/skeleton";

interface BriefingMetrics {
  leadsToday: number;
  leadsYesterday: number;
  leadsTrend: "up" | "down" | "flat";
  revenueThisWeekCents: number;
  revenueLastWeekCents: number;
  revenueTrend: "up" | "down" | "flat";
  openRate: number;
  openRateYesterday: number;
  conversionRate: number;
  conversionRateYesterday: number;
  avgResponseTimeHours: number;
}

const TREND_ARROWS: Record<string, { icon: string; color: string }> = {
  up: { icon: "↑", color: "text-[var(--color-success)]" },
  down: { icon: "↓", color: "text-[var(--color-destructive)]" },
  flat: { icon: "→", color: "text-[var(--color-muted-foreground)]" },
};

export function BriefingMetricsPanel({ metrics }: { metrics: BriefingMetrics | null }) {
  if (!metrics) {
    return (
      <div className="space-y-3 py-1">
        <Skeleton className="h-4 w-24" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between border-b border-[var(--color-border)] pb-1">
              <Skeleton className="h-4 w-24" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-3 w-3 rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-sm font-bold text-[var(--color-muted-foreground)] uppercase tracking-wide mb-3">Key Metrics</h2>
      <div className="space-y-2">
        <MetricRow label="Leads today" value={String(metrics.leadsToday)} prev={String(metrics.leadsYesterday)} trend={metrics.leadsTrend} />
        <MetricRow
          label="Revenue (week)"
          value={fmt(metrics.revenueThisWeekCents)}
          prev={fmt(metrics.revenueLastWeekCents)}
          trend={metrics.revenueTrend}
        />
        <MetricRow label="Open rate" value={pct(metrics.openRate)} prev={pct(metrics.openRateYesterday)} trend={metrics.openRate > metrics.openRateYesterday ? "up" : metrics.openRate < metrics.openRateYesterday ? "down" : "flat"} />
        <MetricRow label="CVR" value={pct(metrics.conversionRate)} prev={pct(metrics.conversionRateYesterday)} trend={metrics.conversionRate > metrics.conversionRateYesterday ? "up" : "flat"} />
        <MetricRow label="Avg response" value={`${metrics.avgResponseTimeHours.toFixed(1)}h`} prev="" trend={metrics.avgResponseTimeHours < 2 ? "up" : "down"} />
      </div>
    </div>
  );
}

function MetricRow({ label, value, prev, trend }: { label: string; value: string; prev: string; trend: string }) {
  const defaultTrend = { icon: "→", color: "text-[var(--color-muted-foreground)]" };
  const t = TREND_ARROWS[trend] ?? defaultTrend;
  return (
    <div className="stagger-item flex items-center justify-between text-sm border-b border-[var(--color-border)] pb-1">
      <span className="text-[var(--color-muted-foreground)]">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-[var(--color-foreground)]">{value}</span>
        <span className={`text-xs ${t.color}`}>{t.icon}</span>
        {prev && <span className="text-[10px] text-[var(--color-muted-foreground)]">(was {prev})</span>}
      </div>
    </div>
  );
}

function fmt(cents: number): string {
  return new Intl.NumberFormat("en", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100);
}

function pct(value: number): string {
  return Math.round(value * 100) + "%";
}
